/**
 * Role Card Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Card displaying individual role information with actions.
 */

import React from 'react';
import { MoreVertical, Users, Shield, Copy, Pencil, Trash2 } from 'lucide-react';
import type { Role } from '../../../types/admin';

interface RoleCardProps {
  role: Role;
  onEdit: (role: Role) => void;
  onDuplicate: (role: Role) => void;
  onDelete: (role: Role) => void;
}

const RoleCard: React.FC<RoleCardProps> = ({
  role,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get permission categories count
  const getPermissionSummary = () => {
    const categories: Record<string, number> = {};
    role.permissions.forEach((perm) => {
      const [category] = perm.split(':');
      categories[category] = (categories[category] || 0) + 1;
    });
    return Object.entries(categories)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([cat]) => cat);
  };

  const permissionCategories = getPermissionSummary();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        {/* Role info */}
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div
            className={`
              w-10 h-10 rounded-lg flex items-center justify-center
              ${role.is_system_role
                ? 'bg-[#002B45] text-[#F7B500]'
                : 'bg-gray-100 text-gray-600'
              }
            `}
          >
            <Shield size={20} />
          </div>

          {/* Details */}
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-gray-900 capitalize">{role.name}</h3>
              {role.is_system_role && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-[#002B45] text-white rounded">
                  System
                </span>
              )}
              {role.is_default && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                  Default
                </span>
              )}
            </div>
            {role.description && (
              <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                {role.description}
              </p>
            )}
          </div>
        </div>

        {/* Actions menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <MoreVertical size={18} />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
              <button
                onClick={() => {
                  onEdit(role);
                  setShowMenu(false);
                }}
                disabled={role.is_system_role}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Pencil size={16} />
                Edit
              </button>

              <button
                onClick={() => {
                  onDuplicate(role);
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Copy size={16} />
                Duplicate
              </button>

              {!role.is_system_role && (
                <>
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={() => {
                      onDelete(role);
                      setShowMenu(false);
                    }}
                    disabled={role.user_count > 0}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={role.user_count > 0 ? 'Cannot delete role with assigned users' : ''}
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <Users size={14} />
          <span>{role.user_count} user{role.user_count !== 1 ? 's' : ''}</span>
        </div>
        <div className="text-sm text-gray-500">
          {role.permissions.length} permission{role.permissions.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Permission categories preview */}
      {permissionCategories.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {permissionCategories.map((cat) => (
            <span
              key={cat}
              className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded capitalize"
            >
              {cat}
            </span>
          ))}
          {role.permissions.length > permissionCategories.length * 2 && (
            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded">
              +more
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default RoleCard;
